import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import moment from 'moment';

import { API_ENDPOINT, SupervisionNoteStatus } from '../../constants';
import { fetchWrapper, toUtcOffsetDisplay } from '../../helpers';
import {
  IParentSupervision,
  IParentGoalProgress,
  ISessionWithClaimStatus,
  ISupervisionWithClaimStatus,
} from '../../utils/types';
import { getBayValueWithoutPrefix, getSupervisionSearchStr } from '../../utils/common';
import {
  isCurrentParentSupervisionSave,
  matchesActiveParentSupervisionNote,
  toParentSupervisionNoteId,
  getNarrativeSuggestionContextKey,
  getParentSupervisionNarrativeSourceKey,
} from '../../utils/narrativeSuggestion';
import { ServiceCodes, SessionClaimStatus } from '../../components/layout/billings/claims/constants';
import { asDefinedOr } from '../../components/layout/billings/claims/utils';
import type {
  ActionReducerMapBuilder,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { NarrativeSuggestionContext } from '../../utils/types';

const name = 'parentSupervisions';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract until typed.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<ParentSupervisionState>) =>
    createExtraReducers(builder),
});

export interface ParentSupervisionState {
  list: Array<IParentSupervision>;
  error: any;
  loading: boolean;
  loadingTargetProgress: boolean;
  current: IParentSupervision | null;
  activeNoteId: number | null;
  supervisionsWithClaimStatus: ISessionWithClaimStatus[];
  narrativeSuggestionLoading: boolean;
  lastAiGeneratedSessionNarrative: string | null;
  narrativeSuggestions: Record<string, string>;
  narrativeSuggestionRequest: (NarrativeSuggestionContext & { requestId: string }) | null;
  narrativeContextToken: string | null;
  narrativeUpdateRequest: {
    parentSupervisionId?: number;
    narrativeContext?: NarrativeSuggestionContext;
    requestId: string;
  } | null;
  // Latest dispatched getParentSupervision fetch. Guards fulfilled/rejected so a stale/out-of-order
  // detail response (e.g. note A's response arriving after note B is already active) never
  // overwrites the currently active note.
  detailRequestId: string | null;
}

type ParentSupervisionStateUpdate = {
  [Key in keyof ParentSupervisionState]: { key: Key; data: ParentSupervisionState[Key] };
}[keyof ParentSupervisionState];

function getSupervisionNoteStatus(status: string): SupervisionNoteStatus | undefined {
  switch (status) {
    case SupervisionNoteStatus.Started:
      return SupervisionNoteStatus.Started;
    case SupervisionNoteStatus.Editing:
      return SupervisionNoteStatus.Editing;
    case SupervisionNoteStatus.Completed:
      return SupervisionNoteStatus.Completed;
    default:
      return undefined;
  }
}

function createInitialState(): ParentSupervisionState {
  return {
    list: [],
    loading: false,
    loadingTargetProgress: false,
    narrativeSuggestionLoading: false,
    lastAiGeneratedSessionNarrative: null,
    narrativeSuggestions: {},
    narrativeSuggestionRequest: null,
    narrativeContextToken: null,
    narrativeUpdateRequest: null,
    error: null,
    current: null,
    activeNoteId: null,
    detailRequestId: null,
    supervisionsWithClaimStatus: [],
  };
}

function createReducers() {
  return {
    clearState,
    updateState,
    setNarrativeSuggestionContext,
    addParentSupervisionGoalProgress,
  };

  function clearState(state: ParentSupervisionState) {
    state.current = null;
    state.activeNoteId = null;
    state.detailRequestId = null;
    state.list = [];
    state.error = null;
    state.loading = false;
    state.narrativeSuggestionLoading = false;
    state.lastAiGeneratedSessionNarrative = null;
    state.narrativeSuggestions = {};
    state.narrativeSuggestionRequest = null;
    state.narrativeContextToken = null;
    state.narrativeUpdateRequest = null;
  }

  function updateState(
    state: ParentSupervisionState,
    action: PayloadAction<ParentSupervisionStateUpdate>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  function setNarrativeSuggestionContext(
    state: ParentSupervisionState,
    action: PayloadAction<NarrativeSuggestionContext>,
  ) {
    const context = action.payload;
    if (state.current?.id !== context.noteId) return;

    const key = getNarrativeSuggestionContextKey(context);
    state.narrativeContextToken = key;
    state.lastAiGeneratedSessionNarrative = state.narrativeSuggestions[key] ?? null;
    if (
      state.narrativeSuggestionRequest &&
      (state.narrativeSuggestionRequest.noteId !== context.noteId ||
        state.narrativeSuggestionRequest.noteType !== context.noteType ||
        state.narrativeSuggestionRequest.sourceKey !== context.sourceKey)
    ) {
      state.narrativeSuggestionLoading = false;
      state.narrativeSuggestionRequest = null;
    }
  }

  function addParentSupervisionGoalProgress(state: ParentSupervisionState, action: any) {
    if (state.current) {
      state.current.goal_progress = action.payload;
    }
  }
}

function normalizeNarrativeSuggestionContext(
  context?: NarrativeSuggestionContext,
): NarrativeSuggestionContext | undefined {
  if (!context) return undefined;
  return {
    ...context,
    contextToken: getNarrativeSuggestionContextKey(context),
  };
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/parent_supervisions`;

  return {
    getList: getList(),
    startParentSupervision: startParentSupervision(),
    getParentSupervision: getParentSupervision(),
    getNarrativeSuggestion: getNarrativeSuggestion(),
    getAggregations: getAggregations(),
    updateParentSupervision: updateParentSupervision(),
    stopParentSupervision: stopParentSupervision(),
    createParentSupervisionGoalProgress: createParentSupervisionGoalProgress(),
    updateParentSupervisionGoalProgress: updateParentSupervisionGoalProgress(),
    deleteParentSupervisionGoalProgress: deleteParentSupervisionGoalProgress(),
    getSupervisionsWithClaimStatus: getSupervisionsWithClaimStatus(),
    deleteParentSupervision: deleteParentSupervision(),
    createTargetEvaluation: createTargetEvaluation(),
    updateTargetEvaluation: updateTargetEvaluation(),
    deleteTargetEvaluation: deleteTargetEvaluation(),
    createRateEvent: createRateEvent(),
    updateRateEvent: updateRateEvent(),
    deleteRateEvent: deleteRateEvent(),
    createDurationEvent: createDurationEvent(),
    updateDurationEvent: updateDurationEvent(),
    deleteDurationEvent: deleteDurationEvent(),
    downloadPDF: downloadPDF(),
    editParentSupervision: editParentSupervision(),
    backdateParentSupervision: backdateParentSupervision(),
  };

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ params, getAccessTokenSilently, signal }: any) => {
        let baseSearch = `${baseUrl}/?`;
        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }
        const response = await fetchWrapper.get({
          url: baseSearch,
          getAccessTokenSilently,
          signal,
        });
        return response;
      },
    );
  }

  function startParentSupervision() {
    return createAsyncThunk(
      `${name}/startParentSupervision`,
      async ({ data, getAccessTokenSilently }: any) =>
        await fetchWrapper.post({ url: `${baseUrl}/`, getAccessTokenSilently }, data),
    );
  }

  function getParentSupervision() {
    return createAsyncThunk(
      `${name}/getParentSupervision`,
      async ({ parentSupervisionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.get({
          url: `${baseUrl}/${parentSupervisionId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function getNarrativeSuggestion() {
    return createAsyncThunk(
      `${name}/getNarrativeSuggestion`,
      async ({ parentSupervisionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.post({
          url: `${API_ENDPOINT}/narrative_suggestion/97156/${parentSupervisionId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function getAggregations() {
    return createAsyncThunk(
      `${name}/getAggregations`,
      async ({ getAccessTokenSilently, clientId, start, end }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/data/utilization/?client_id=${clientId}&start=${start}&end=${end}`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function updateParentSupervision() {
    return createAsyncThunk(
      `${name}/updateParentSupervision`,
      async ({ parentSupervisionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.patch(
          { url: `${baseUrl}/${parentSupervisionId}/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function stopParentSupervision() {
    return createAsyncThunk(
      `${name}/stopParentSupervision`,
      async ({ parentSupervisionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.put(
          { url: `${baseUrl}/${parentSupervisionId}/stop/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function createParentSupervisionGoalProgress() {
    return createAsyncThunk(
      `${name}/createParentSupervisionGoalProgress`,
      async ({ data, getAccessTokenSilently, parentSupervisionId }: any) =>
        await fetchWrapper.post(
          { url: `${baseUrl}/${parentSupervisionId}/goal_progress/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function updateParentSupervisionGoalProgress() {
    return createAsyncThunk(
      `${name}/updateParentSupervisionGoalProgress`,
      async ({ data, getAccessTokenSilently, parentSupervisionId, goalProgressId }: any) =>
        await fetchWrapper.patch(
          {
            url: `${baseUrl}/${parentSupervisionId}/goal_progress/${goalProgressId}/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function deleteParentSupervisionGoalProgress() {
    return createAsyncThunk(
      `${name}/deleteParentSupervisionGoalProgress`,
      async ({ getAccessTokenSilently, parentSupervisionId, goalProgressId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${parentSupervisionId}/goal_progress/${goalProgressId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function getSupervisionsWithClaimStatus() {
    return createAsyncThunk(
      `${name}/getSupervisionsWithClaimStatus`,
      async ({ params, getAccessTokenSilently }: any) => {
        let baseSearch = `${baseUrl}/claim_history/?`;

        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }

        const response = await fetchWrapper.get({ url: baseSearch, getAccessTokenSilently });
        return response;
      },
    );
  }

  function deleteParentSupervision() {
    return createAsyncThunk(
      `${name}/deleteParentSupervision`,
      async ({ parentSupervisionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${parentSupervisionId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function createTargetEvaluation() {
    return createAsyncThunk(
      `${name}/createTargetEvaluation`,
      async ({ data, getAccessTokenSilently, parentSupervisionId }: any) =>
        await fetchWrapper.post(
          { url: `${baseUrl}/${parentSupervisionId}/target_evaluations/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function updateTargetEvaluation() {
    return createAsyncThunk(
      `${name}/updateTargetEvaluation`,
      async ({ data, getAccessTokenSilently, parentSupervisionId, targetEvaluationId }: any) =>
        await fetchWrapper.patch(
          {
            url: `${baseUrl}/${parentSupervisionId}/target_evaluations/${targetEvaluationId}/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function deleteTargetEvaluation() {
    return createAsyncThunk(
      `${name}/deleteTargetEvaluation`,
      async ({ getAccessTokenSilently, parentSupervisionId, targetEvaluationId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${parentSupervisionId}/target_evaluations/${targetEvaluationId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function createRateEvent() {
    return createAsyncThunk(
      `${name}/createRateEvent`,
      async ({ data, getAccessTokenSilently, parentSupervisionId, trailId }: any) =>
        await fetchWrapper.post(
          {
            url: `${baseUrl}/${parentSupervisionId}/target_evaluations/${trailId}/rate_events/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function updateRateEvent() {
    return createAsyncThunk(
      `${name}/updateRateEvent`,
      async ({ data, getAccessTokenSilently, parentSupervisionId, trailId, rateEventId }: any) =>
        await fetchWrapper.patch(
          {
            url: `${baseUrl}/${parentSupervisionId}/target_evaluations/${trailId}/rate_events/${rateEventId}/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function deleteRateEvent() {
    return createAsyncThunk(
      `${name}/deleteRateEvent`,
      async ({ getAccessTokenSilently, parentSupervisionId, trailId, rateEventId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${parentSupervisionId}/target_evaluations/${trailId}/rate_events/${rateEventId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function createDurationEvent() {
    return createAsyncThunk(
      `${name}/createDurationEvent`,
      async ({ data, getAccessTokenSilently, parentSupervisionId, trailId }: any) =>
        await fetchWrapper.post(
          {
            url: `${baseUrl}/${parentSupervisionId}/target_evaluations/${trailId}/duration_events/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function updateDurationEvent() {
    return createAsyncThunk(
      `${name}/updateDurationEvent`,
      async ({
        data,
        getAccessTokenSilently,
        parentSupervisionId,
        trailId,
        durationEventId,
      }: any) =>
        await fetchWrapper.patch(
          {
            url: `${baseUrl}/${parentSupervisionId}/target_evaluations/${trailId}/duration_events/${durationEventId}/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function deleteDurationEvent() {
    return createAsyncThunk(
      `${name}/deleteDurationEvent`,
      async ({ getAccessTokenSilently, parentSupervisionId, trailId, durationEventId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${parentSupervisionId}/target_evaluations/${trailId}/duration_events/${durationEventId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function downloadPDF() {
    return createAsyncThunk(
      `${name}/downloadPDF`,
      async ({ supervisionId, getAccessTokenSilently }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/${supervisionId}/download/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function editParentSupervision() {
    return createAsyncThunk(
      `${name}/editParentSupervision`,
      async ({ supervisionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.put({
          url: `${baseUrl}/${supervisionId}/edit/`,
          getAccessTokenSilently,
        }),
    );
  }

  function backdateParentSupervision() {
    return createAsyncThunk(
      `${name}/backdateParentSupervision`,
      async ({ supervisionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.put(
          {
            url: `${baseUrl}/${supervisionId}/backdate/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<ParentSupervisionState>) {
  const {
    getList,
    startParentSupervision,
    getParentSupervision,
    getNarrativeSuggestion,
    updateParentSupervision,
    createParentSupervisionGoalProgress,
    updateParentSupervisionGoalProgress,
    deleteParentSupervisionGoalProgress,
    getSupervisionsWithClaimStatus,
    deleteParentSupervision,
    createTargetEvaluation,
    updateTargetEvaluation,
    deleteTargetEvaluation,
    stopParentSupervision,
    getAggregations,
    createRateEvent,
    updateRateEvent,
    deleteRateEvent,
    createDurationEvent,
    updateDurationEvent,
    deleteDurationEvent,
    downloadPDF,
    editParentSupervision,
    backdateParentSupervision,
  } = extraActions;

  builder
    //getList
    .addCase(getList.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getList.fulfilled,
      (state: ParentSupervisionState, action: PayloadAction<IParentSupervision[]>) => {
        state.list = action.payload;
        state.loading = false;
      },
    )
    .addCase(getList.rejected, (state: ParentSupervisionState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getAggregations()
    .addCase(getAggregations.pending, (state: ParentSupervisionState) => {
      state.error = null;
    })
    .addCase(getAggregations.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
    })

    // createTargetEvaluation()
    .addCase(createTargetEvaluation.pending, (state: ParentSupervisionState) => {
      state.loadingTargetProgress = true;
      state.error = null;
    })
    .addCase(createTargetEvaluation.fulfilled, (state: ParentSupervisionState) => {
      state.loadingTargetProgress = false;
    })
    .addCase(createTargetEvaluation.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loadingTargetProgress = false;
      toast.error(state.error.message);
    })

    // stopParentSupervision()
    .addCase(stopParentSupervision.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(stopParentSupervision.fulfilled, (state: ParentSupervisionState) => {
      state.loading = false;
    })
    .addCase(stopParentSupervision.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateTargetEvaluation()
    .addCase(updateTargetEvaluation.pending, (state: ParentSupervisionState) => {
      state.loadingTargetProgress = true;
      state.error = null;
    })
    .addCase(updateTargetEvaluation.fulfilled, (state: ParentSupervisionState, action: any) => {
      state.loadingTargetProgress = false;
      if (state.current) {
        state.current = { ...state.current, target_progress: action.payload.target_progress };
      }
    })
    .addCase(updateTargetEvaluation.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loadingTargetProgress = false;
      toast.error(state.error.message);
    })

    // deleteTargetEvaluation()
    .addCase(deleteTargetEvaluation.pending, (state: ParentSupervisionState) => {
      state.loadingTargetProgress = true;
      state.error = null;
    })
    .addCase(deleteTargetEvaluation.fulfilled, (state: ParentSupervisionState, action: any) => {
      state.loadingTargetProgress = false;
      if (state.current) {
        state.current = { ...state.current, target_progress: action.payload.target_progress };
      }
    })
    .addCase(deleteTargetEvaluation.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loadingTargetProgress = false;
      toast.error(state.error.message);
    })

    // startParentSupervision()
    .addCase(startParentSupervision.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      startParentSupervision.fulfilled,
      (state: ParentSupervisionState, action: PayloadAction<IParentSupervision>) => {
        state.loading = false;
        state.current = action.payload;
        state.activeNoteId = toParentSupervisionNoteId(action.payload.id);
        toast.success('Parent Supervision started');
      },
    )
    .addCase(startParentSupervision.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getParentSupervision()
    // Guard by requestId (mirrors the client.slice.ts modifier-matrix pattern) so a stale
    // detail response can never overwrite a newer note that is already active.
    .addCase(getParentSupervision.pending, (state: ParentSupervisionState, action: any) => {
      state.loading = true;
      state.error = null;
      state.detailRequestId = action.meta.requestId;
      state.narrativeSuggestionLoading = false;
      state.narrativeSuggestionRequest = null;
      state.narrativeContextToken = null;
      state.activeNoteId = toParentSupervisionNoteId(action.meta.arg.parentSupervisionId);
      state.narrativeUpdateRequest = null;
    })
    .addCase(
      getParentSupervision.fulfilled,
      (
        state: ParentSupervisionState,
        action: PayloadAction<any> & {
          meta: { requestId: string; arg: { parentSupervisionId?: number | string } };
        },
      ) => {
        if (
          action.meta.requestId !== state.detailRequestId ||
          !matchesActiveParentSupervisionNote(state.activeNoteId, action.meta.arg.parentSupervisionId)
        ) {
          return;
        }
        state.current = action.payload;
        state.narrativeContextToken = null;
        const sourceKey = getParentSupervisionNarrativeSourceKey(action.payload);
        const contextKey = getNarrativeSuggestionContextKey({
          noteId: action.payload.id,
          noteType: '97156',
          sourceKey,
        });
        if (action.payload.last_ai_generated_session_narrative !== undefined) {
          if (action.payload.last_ai_generated_session_narrative === null) {
            delete state.narrativeSuggestions[contextKey];
          } else {
            state.narrativeSuggestions[contextKey] =
              action.payload.last_ai_generated_session_narrative;
          }
        }
        state.lastAiGeneratedSessionNarrative =
          state.narrativeSuggestions[contextKey] ?? null;
        state.error = null;
        state.loading = false;
      },
    )
    .addCase(getParentSupervision.rejected, (state: ParentSupervisionState, action: any) => {
      if (
        action.meta.requestId !== state.detailRequestId ||
        !matchesActiveParentSupervisionNote(state.activeNoteId, action.meta.arg.parentSupervisionId)
      ) {
        return;
      }
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // getNarrativeSuggestion()
    .addCase(getNarrativeSuggestion.pending, (state: ParentSupervisionState, action: any) => {
      const { noteId, noteType, sourceKey } = action.meta.arg;
      const context = normalizeNarrativeSuggestionContext(action.meta.arg);
      state.narrativeSuggestionRequest = {
        noteId,
        noteType,
        sourceKey,
        contextToken: context?.contextToken,
        requestId: action.meta.requestId,
      };
      state.narrativeSuggestionLoading = state.current?.id === noteId;
    })
    .addCase(
      getNarrativeSuggestion.fulfilled,
      (state: ParentSupervisionState, action: any) => {
        const { noteId, noteType, sourceKey } = action.meta.arg;
        const context = normalizeNarrativeSuggestionContext(action.meta.arg);
        const request = state.narrativeSuggestionRequest;
        if (
          state.current?.id === noteId &&
          request !== null &&
          request.requestId === action.meta.requestId &&
          request.noteType === noteType &&
          request.sourceKey === sourceKey &&
          request.contextToken === context?.contextToken
        ) {
          const contextKey = getNarrativeSuggestionContextKey({ noteId, noteType, sourceKey });
          state.narrativeSuggestions[contextKey] = action.payload.narrative;
          state.narrativeSuggestionLoading = false;
          state.lastAiGeneratedSessionNarrative = action.payload.narrative;
        }
      },
    )
    .addCase(getNarrativeSuggestion.rejected, (state: ParentSupervisionState, action: any) => {
      if (state.narrativeSuggestionRequest?.requestId === action.meta.requestId) {
        state.narrativeSuggestionLoading = false;
        state.narrativeSuggestionRequest = null;
      }
    })

    // updateParentSupervision()
    .addCase(updateParentSupervision.pending, (state: ParentSupervisionState, action: any) => {
      state.error = null;
      state.loading = true;
      const narrativeContext = normalizeNarrativeSuggestionContext(
        action.meta.arg.context ?? action.meta.arg.narrativeContext,
      );
      state.narrativeUpdateRequest = {
        parentSupervisionId: action.meta.arg.parentSupervisionId,
        narrativeContext,
        requestId: action.meta.requestId,
      };
    })
    .addCase(updateParentSupervision.fulfilled, (state: ParentSupervisionState, action: any) => {
      const savedNoteId = action.meta.arg.parentSupervisionId;
      if (!matchesActiveParentSupervisionNote(state.activeNoteId, savedNoteId)) return;
      const saveContext = action.meta.arg.context ?? action.meta.arg.narrativeContext;
      if (!isCurrentParentSupervisionSave(state.current, savedNoteId, saveContext)) {
        if (state.current?.id === savedNoteId) {
          state.loading = false;
        }
        return;
      }
      const { parentSupervisionId } = action.meta.arg;
      const narrativeContext = normalizeNarrativeSuggestionContext(
        action.meta.arg.context ?? action.meta.arg.narrativeContext,
      );
      const request = state.narrativeUpdateRequest;
      const isCurrentRequest =
        request?.requestId === action.meta.requestId &&
        request?.parentSupervisionId === parentSupervisionId &&
        state.current?.id === parentSupervisionId;
      const isCurrentNarrativeContext =
        !narrativeContext ||
        (request?.narrativeContext?.noteId === narrativeContext.noteId &&
          request?.narrativeContext?.noteType === narrativeContext.noteType &&
          request?.narrativeContext?.sourceKey === narrativeContext.sourceKey &&
          request?.narrativeContext?.contextToken === narrativeContext.contextToken &&
          state.current?.id === narrativeContext.noteId &&
          state.narrativeContextToken === narrativeContext.contextToken);
      if (!isCurrentRequest || !isCurrentNarrativeContext) {
        if (request?.requestId === action.meta.requestId) {
          state.narrativeUpdateRequest = null;
          state.loading = false;
        }
        return;
      }
      state.error = null;
      const data = { ...action.payload };
      delete data.goal_progress;
      state.current = { ...state.current, ...data };
      if (
        state.current?.id !== undefined &&
        action.payload.last_ai_generated_session_narrative !== undefined
      ) {
        const sourceKey = getParentSupervisionNarrativeSourceKey(state.current);
        const contextKey = getNarrativeSuggestionContextKey({
          noteId: state.current.id,
          noteType: '97156',
          sourceKey,
        });
        if (action.payload.last_ai_generated_session_narrative === null) {
          delete state.narrativeSuggestions[contextKey];
        } else {
          state.narrativeSuggestions[contextKey] =
            action.payload.last_ai_generated_session_narrative;
        }
        state.lastAiGeneratedSessionNarrative = state.narrativeSuggestions[contextKey] ?? null;
      }
      state.loading = false;
      state.narrativeUpdateRequest = null;
    })
    .addCase(updateParentSupervision.rejected, (state: ParentSupervisionState, action: any) => {
      const savedNoteId = action.meta.arg.parentSupervisionId;
      if (!matchesActiveParentSupervisionNote(state.activeNoteId, savedNoteId)) return;
      if (
        !isCurrentParentSupervisionSave(
          state.current,
          savedNoteId,
          action.meta.arg.context ?? action.meta.arg.narrativeContext,
        )
      ) {
        if (state.current?.id === savedNoteId) {
          state.loading = false;
        }
        return;
      }
      if (state.narrativeUpdateRequest?.requestId !== action.meta.requestId) return;
      state.error = action.error;
      state.loading = false;
      state.narrativeUpdateRequest = null;
      toast.error(action.error.message);
    })
    //createParentSupervisionGoalProgress
    .addCase(createParentSupervisionGoalProgress.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createParentSupervisionGoalProgress.fulfilled,
      (state: ParentSupervisionState, action: any) => {
        state.loading = false;
        if (state.current) {
          state.current = state.current?.goal_progress
            ? {
                ...state.current,
                goal_progress: state.current.goal_progress.map(
                  (goal: IParentGoalProgress, i: number) => {
                    if (i === action.meta.arg.index) {
                      return action.payload;
                    }
                    return goal;
                  },
                ),
              }
            : {
                ...state.current,
                goal_progress: [action.payload],
              };
        }
      },
    )
    .addCase(
      createParentSupervisionGoalProgress.rejected,
      (state: ParentSupervisionState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(state.error.message);
      },
    )

    // updateParentSupervisionGoalProgress()
    .addCase(updateParentSupervisionGoalProgress.pending, (state: ParentSupervisionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      updateParentSupervisionGoalProgress.fulfilled,
      (state: ParentSupervisionState, action: any) => {
        state.error = null;
        state.loading = false;
        if (state.current?.goal_progress) {
          const { goal_progress } = state.current;
          const updatedProgress = goal_progress.map((progress) => {
            if (progress.id === action.meta.arg.goalProgressId) {
              return action.payload;
            }
            return progress;
          });
          state.current = { ...state.current, goal_progress: updatedProgress };
        }
      },
    )
    .addCase(
      updateParentSupervisionGoalProgress.rejected,
      (state: ParentSupervisionState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(action.error.message);
      },
    )

    // deleteParentSupervisionGoalProgress()
    .addCase(deleteParentSupervisionGoalProgress.pending, (state: ParentSupervisionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      deleteParentSupervisionGoalProgress.fulfilled,
      (state: ParentSupervisionState, action: any) => {
        state.error = null;
        state.loading = false;
        if (state.current?.goal_progress) {
          const { goal_progress } = state.current;
          const updatedProgress = goal_progress.filter(
            (progress) => progress.id !== action.meta.arg.goalProgressId,
          );
          state.current = { ...state.current, goal_progress: updatedProgress };
        }
      },
    )
    .addCase(
      deleteParentSupervisionGoalProgress.rejected,
      (state: ParentSupervisionState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(action.error.message);
      },
    )

    // getSupervisionsWithClaimStatus()
    .addCase(getSupervisionsWithClaimStatus.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getSupervisionsWithClaimStatus.fulfilled,
      (state: ParentSupervisionState, action: PayloadAction<ISupervisionWithClaimStatus[]>) => {
        state.loading = false;
        state.supervisionsWithClaimStatus = action.payload.map(
          (supervision: ISupervisionWithClaimStatus) => {
            return {
              id: supervision.id,
              date: moment(supervision.date_start).format(),
              status:
                supervision.claim_history && supervision.claim_history?.length > 0
                  ? supervision.claim_history[0].status
                  : SessionClaimStatus.Unclaimed,
              selected: false,
              readonly: Boolean(supervision.claim_id),
              location: supervision.location,
              duration: supervision.expected_duration ?? 0,
              provider_name: `${supervision.bcba.first_name} ${supervision.bcba.last_name} `,
              provider_id: supervision.bcba.id,
              author_id: supervision.bcba.id, // bcba is the author
              bay: getBayValueWithoutPrefix(supervision.bay),
              timeZone: supervision.utc_offset_date_start
                ? toUtcOffsetDisplay(asDefinedOr(supervision.utc_offset_date_start, 0))
                : '0:0',
              sessionStatus: getSupervisionNoteStatus(supervision.status),
              procedureCode: ServiceCodes.ParentSNProcedureCode,
            };
          },
        );
      },
    )
    .addCase(
      getSupervisionsWithClaimStatus.rejected,
      (state: ParentSupervisionState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(action.error.message, {
          toastId: 'parent-supervisions-sessions-with-claim-status-error',
        });
      },
    )

    // deleteParentSupervision()
    .addCase(deleteParentSupervision.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteParentSupervision.fulfilled, (state: ParentSupervisionState) => {
      state.loading = false;
      state.current = null;
      state.activeNoteId = null;
      toast.success('Parent supervision deleted successfully');
    })
    .addCase(deleteParentSupervision.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // downloadPDF()
    .addCase(downloadPDF.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(downloadPDF.fulfilled, (state: ParentSupervisionState) => {
      state.loading = false;
      state.error = null;
      toast.success('Parent supervision downloaded successfully');
    })
    .addCase(downloadPDF.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // createRateEvent()
    .addCase(createRateEvent.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createRateEvent.fulfilled,
      (state: ParentSupervisionState, action: PayloadAction<any>) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            target_progress: action.payload.target_progress,
          };
        }
        toast.success('Rate event created successfully');
      },
    )
    .addCase(createRateEvent.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // updateRateEvent()
    .addCase(updateRateEvent.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateRateEvent.fulfilled,
      (state: ParentSupervisionState, action: PayloadAction<any>) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            target_progress: action.payload.target_progress,
          };
        }
      },
    )
    .addCase(updateRateEvent.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // deleteRateEvent()
    .addCase(deleteRateEvent.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteRateEvent.fulfilled,
      (state: ParentSupervisionState, action: PayloadAction<any>) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            target_progress: action.payload.target_progress,
          };
        }
        toast.success('Rate event deleted successfully');
      },
    )
    .addCase(deleteRateEvent.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // createDurationEvent()
    .addCase(createDurationEvent.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createDurationEvent.fulfilled,
      (state: ParentSupervisionState, action: PayloadAction<any>) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            target_progress: action.payload.target_progress,
          };
        }
        toast.success('Duration event created successfully');
      },
    )
    .addCase(createDurationEvent.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // updateDurationEvent()
    .addCase(updateDurationEvent.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateDurationEvent.fulfilled,
      (state: ParentSupervisionState, action: PayloadAction<any>) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            target_progress: action.payload.target_progress,
          };
        }
      },
    )
    .addCase(updateDurationEvent.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // deleteDurationEvent()
    .addCase(deleteDurationEvent.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteDurationEvent.fulfilled,
      (state: ParentSupervisionState, action: PayloadAction<any>) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            target_progress: action.payload.target_progress,
          };
        }
        toast.success('Duration event deleted successfully');
      },
    )
    .addCase(deleteDurationEvent.rejected, (state: ParentSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // editParentSupervision()
    .addCase(editParentSupervision.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(editParentSupervision.fulfilled, (state: ParentSupervisionState) => {
      state.loading = false;
      state.error = null;
      toast.success('Parent supervision note is now editable');
    })
    .addCase(editParentSupervision.rejected, (state: ParentSupervisionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    })

    // backdateParentSupervision()
    .addCase(backdateParentSupervision.pending, (state: ParentSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(backdateParentSupervision.fulfilled, (state: ParentSupervisionState) => {
      state.loading = false;
      state.error = null;
      toast.success('The start date of parent supervision note has been updated');
    })
    .addCase(backdateParentSupervision.rejected, (state: ParentSupervisionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    });
}

export const parentSupervisionActions = { ...slice.actions, ...extraActions };
export const parentSupervisionReducer = slice.reducer;
